import {
  html,
  type ReactiveController,
  type ReactiveControllerHost,
  type TemplateResult,
} from "lit";
import { cn } from "@/utilities/css";
import { NPS_SCORES, type FeedbackWidgetSubmitDetail } from "../types";

interface NpsFeedbackCallbacks {
  onCancel: (e: Event) => void;
  onSubmit: (detail: FeedbackWidgetSubmitDetail) => void;
}

export class NpsFeedbackController implements ReactiveController {
  private _npsDontAsk = false;
  private _npsFeedback = "";
  private _npsRating: number | undefined = undefined;

  private _onDontAskChange = (e: CustomEvent<{ checked: boolean }>) => {
    this._npsDontAsk = e.detail.checked;
    this.host.requestUpdate();
  };

  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") {
      this._npsFeedback = e.detail.value;
      this.host.requestUpdate();
    }
  };

  private _onSubmit = () => {
    // === undefined not falsy: 0 is a valid NPS score
    if (this._npsRating === undefined && !this._npsDontAsk) return;
    this.callbacks.onSubmit({
      dontAskAgain: this._npsDontAsk,
      feedback: this._npsFeedback,
      feedbackType: "nps",
      rating: this._npsRating,
    });
  };

  constructor(
    private host: ReactiveControllerHost & HTMLElement,
    private callbacks: NpsFeedbackCallbacks
  ) {
    host.addController(this);
  }

  private _onRatingKeydown(e: KeyboardEvent, score: number) {
    const ratings = Array.from(
      this.host.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-widget__nps-rating")
    );
    const index = NPS_SCORES.indexOf(score);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % ratings.length;
        this._npsRating = NPS_SCORES[nextIndex];
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + ratings.length) % ratings.length;
        this._npsRating = NPS_SCORES[prevIndex];
        this.host.requestUpdate();
        this.host.updateComplete.then(() => ratings[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this._npsRating = score;
        this.host.requestUpdate();
        break;
    }
  }

  private _renderRating(score: number): TemplateResult {
    const selected = this._npsRating === score;
    const tabindex = selected || (this._npsRating === undefined && score === 0) ? 0 : -1;
    const label =
      score === 0
        ? "0 - Not at all likely"
        : score === 10
          ? "10 - Extremely likely"
          : String(score);
    return html`
      <div
        class=${cn("eko-feedback-widget__nps-rating", {
          "eko-feedback-widget__nps-rating--selected": selected,
        })}
        role="radio"
        aria-checked=${String(selected)}
        aria-label=${label}
        tabindex=${tabindex}
        @click=${() => {
          this._npsRating = score;
          this.host.requestUpdate();
        }}
        @keydown=${(e: KeyboardEvent) => this._onRatingKeydown(e, score)}>
        ${score}
      </div>
    `;
  }

  private _resolvePlaceholder(
    negative: string | undefined,
    neutral: string | undefined,
    positive: string | undefined
  ): string {
    if (this._npsRating !== undefined) {
      if (this._npsRating <= 6 && negative) return negative;
      if (this._npsRating <= 8 && neutral) return neutral;
      if (this._npsRating >= 9 && positive) return positive;
    }
    return "Provide feedback (optional)";
  }

  hostConnected() {}

  render(params: {
    errorMessage: string | undefined;
    followUpNegative: string | undefined;
    followUpNeutral: string | undefined;
    followUpPositive: string | undefined;
    loading: boolean;
    question: string;
  }): TemplateResult {
    const { errorMessage, followUpNegative, followUpNeutral, followUpPositive, loading, question } =
      params;

    const placeholder = this._resolvePlaceholder(
      followUpNegative,
      followUpNeutral,
      followUpPositive
    );

    return html`
      <div slot="title" class="eko-feedback-widget__nps-header">
        <span class="eko-feedback-widget__nps-label">
          <eko-icon name="message-question-circle" size="sm"></eko-icon>
          Help us improve your experience
        </span>
        <span class="eko-feedback-widget__nps-question">${question}</span>
      </div>

      <div class="eko-feedback-widget__nps-body">
        <div class="eko-feedback-widget__nps-ratings" role="radiogroup" aria-label=${question}>
          ${NPS_SCORES.map((s) => this._renderRating(s))}
        </div>
        ${errorMessage ? html`<eko-alert variant="danger">${errorMessage}</eko-alert>` : html``}
        <eko-textarea
          placeholder=${placeholder}
          .value=${this._npsFeedback}
          .rows=${3}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div slot="footer" class="eko-feedback-widget__nps-footer">
        <eko-checkbox .checked=${this._npsDontAsk} @change=${this._onDontAskChange}>
          <span slot="label">Don't ask me again</span>
        </eko-checkbox>
        <eko-button
          variant="primary"
          ?disabled=${(this._npsRating === undefined && !this._npsDontAsk) || loading}
          ?loading=${loading}
          @click=${this._onSubmit}>
          Submit
        </eko-button>
      </div>
    `;
  }

  reset() {
    this._npsRating = undefined;
    this._npsFeedback = "";
    this._npsDontAsk = false;
  }
}
